import { createContext, ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getConfig, getConfigSchema } from '@/api/bff';
import { ErrorScreen } from '@/components/ErrorScreen/ErrorScreen';
import { FullScreenLoader } from '@/components/LoadScreen/FullScreenLoader';
import { useAirKeyTrace } from '@/utils/airKeyTrace';
import { ConfigSchemaV1Type } from './ConfigSchemaV1Type';
import { ConfigV1Type } from './ConfigV1Type';

const SUPPORTED_LOCALES = { 'en-US': true, 'es-US': true } as const;

export type SupportedLocales = keyof typeof SUPPORTED_LOCALES;

function detectLocale(): SupportedLocales {
  const lang = navigator.language;
  return (lang in SUPPORTED_LOCALES ? lang : 'en-US') as SupportedLocales;
}

export type ConfigContextType = {
  configSchema: ConfigSchemaV1Type;
  locale: SupportedLocales;
} & ConfigV1Type;

export const ConfigContext = createContext<ConfigContextType | undefined>(
  undefined,
);

/**
 * A provider component that supplies both the configSchema and the use-case specific config to its children.
 */
export function ConfigProvider({ children }: { children: ReactNode }) {
  const { intentOwner, workflow } = useParams();
  const [configContext, setConfigContext] = useState<ConfigContextType>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const sendTrace = useAirKeyTrace();

  useEffect(() => {
    async function fetchConfigAndSchema() {
      if (!intentOwner || !workflow) return;

      const startTime = performance.now();
      try {
        const [configSchema, config] = await Promise.all([
          getConfigSchema(),
          getConfig(intentOwner, workflow),
        ]);
        const loadTime = performance.now() - startTime;
        const locale = detectLocale();

        sendTrace({
          view: 'loading',
          action: 'system_initialized',
          additionalData: {
            component: 'ConfigProvider',
            config_load_time_ms: Math.round(loadTime),
            template: config?.template,
            intent_owner: intentOwner,
            workflow_type: workflow,
            locale: locale,
          },
        });

        setConfigContext({
          configSchema,
          locale,
          ...config,
          content: {
            ...config.content,
            config: {
              ...config.content.config,
              urlBase: `${config.content.config.urlBase}${intentOwner}/${workflow}/`,
            },
          },
        });
      } catch (e) {
        console.error('Error fetching config', e);
        setError(true);
      } finally {
        setLoading(false);
      }
    }

    fetchConfigAndSchema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Note: Depedencies omitted intentionally - URL params never change without component remount

  if (error) {
    return <ErrorScreen />;
  }

  if (loading) {
    return <FullScreenLoader />;
  }

  return (
    <ConfigContext.Provider value={configContext}>
      {children}
    </ConfigContext.Provider>
  );
}
